import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CronStatusChip from './CronStatusChip';
import LogLevelChip from '../../logs/components/LogLevelChip';
import { formatBackupSize } from '../../backups/components/BackupRow';

/**
 * Formata 'YYYY-MM-DD HH:MM:SS' (fuso do servidor) como 'DD/MM HH:MM', sem passar por
 * Date/fuso do navegador (mesma estratégia de formatLogTime em LogRow).
 * @param {string} dateStr
 * @returns {string}
 */
function formatShortDateTime(dateStr) {
  if (!dateStr || dateStr.length < 16) return '—';
  const [, month, day] = dateStr.slice(0, 10).split('-');
  return `${day}/${month} ${dateStr.slice(11, 16)}`;
}

/**
 * Tempo relativo (há X min/h/d) a partir de uma data 'YYYY-MM-DD HH:MM:SS'.
 * @param {string} dateStr
 * @returns {string}
 */
function formatRelative(dateStr) {
  if (!dateStr) return '';
  const then = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(then.getTime())) return '';
  const diffMin = Math.round((Date.now() - then.getTime()) / 60000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  return `há ${Math.round(diffH / 24)} d`;
}

/**
 * Formata a duração de uma execução em segundos como '3s' ou '2m 5s'.
 * @param {number|null} seconds
 * @returns {string}
 */
function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/**
 * Tempo relativo (em X min/h) até uma data futura 'YYYY-MM-DD HH:MM:SS'.
 * @param {string} dateStr
 * @returns {string}
 */
function formatFutureRelative(dateStr) {
  if (!dateStr) return '';
  const then = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(then.getTime())) return '';
  const diffMin = Math.round((then.getTime() - Date.now()) / 60000);
  if (diffMin < 1) return 'em instantes';
  if (diffMin < 60) return `em ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  return `em ${diffH} h`;
}

/**
 * Formata a idade de um lock em segundos como '45s' ou '3 min'.
 * @param {number|null} seconds
 * @returns {string}
 */
function formatLockAge(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.round(seconds / 60)} min`;
}

/**
 * CronRow - Linha da tabela de rotinas (Admin Geral > Rotinas)
 *
 * Linha expansível: descrição, estado do lock, artefato (backup) e últimas mensagens
 * de aviso/erro da execução, com atalho para o viewer de Logs filtrado por req.
 *
 * @param {Object} props
 * @param {Object} props.routine - item de `routines` retornado por status.php
 * @param {Function} props.onRunNow - "Rodar agora" (a página confirma e dispara)
 */
function CronRow({ routine, onRunNow }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const {
    script, label, description, schedule,
    status, last_run: lastRun, runs_in_window: runsInWindow, lock, artifact,
  } = routine;

  const handleViewLogs = (e) => {
    e.stopPropagation();
    if (!lastRun) return;
    navigate(`/admin/logs?date=${lastRun.date}&req=${lastRun.req}`);
  };

  // Lock dentro do TTL: a rotina está rodando e o script recusaria uma segunda execução.
  const isRunning = Boolean(lock?.exists && !lock?.stale);

  const handleRunNow = (e) => {
    e.stopPropagation();
    if (isRunning || !onRunNow) return;
    onRunNow(routine);
  };

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setExpanded((prev) => !prev)}>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
            {script}
          </Typography>
        </TableCell>

        <TableCell>
          {schedule ? (
            <>
              <Typography variant="body2">{schedule.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                {schedule.expression}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.disabled">Não agendado</Typography>
              <Typography variant="caption" color="text.disabled">fora do crontab</Typography>
            </>
          )}
        </TableCell>

        <TableCell>
          {lastRun ? (
            <>
              <Typography variant="body2">{formatShortDateTime(lastRun.started_at)}</Typography>
              <Typography variant="caption" color="text.secondary">{formatRelative(lastRun.started_at)}</Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {lastRun ? formatDuration(lastRun.duration_seconds) : '—'}
          </Typography>
        </TableCell>

        <TableCell><CronStatusChip status={status} /></TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {lastRun ? `${lastRun.errors} / ${lastRun.warnings}` : '— / —'}
          </Typography>
          <Typography variant="caption" color="text.disabled">{runsInWindow} execuções</Typography>
        </TableCell>

        <TableCell align="right">
          <Tooltip title={isRunning ? 'Já está rodando' : 'Rodar agora'}>
            <span>
              <IconButton size="small" color="primary" onClick={handleRunNow} disabled={isRunning} aria-label="Rodar agora">
                <PlayArrowOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, border: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#F8F9FC' }}>
              {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{description}</Typography>
              )}

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
                <Typography variant="body2">
                  <strong>Lock:</strong>{' '}
                  {lock?.exists
                    ? `existe há ${formatLockAge(lock.age_seconds)}${lock.stale ? ' (preso)' : ''}`
                    : 'não existe'}
                </Typography>
                {schedule?.next_run && (
                  <Typography variant="body2">
                    <strong>Próxima execução:</strong>{' '}
                    {formatShortDateTime(schedule.next_run)} ({formatFutureRelative(schedule.next_run)})
                  </Typography>
                )}
                {schedule?.runs_per_day != null && (
                  <Typography variant="body2">
                    <strong>Execuções por dia:</strong> {schedule.runs_per_day}
                  </Typography>
                )}
                {artifact && (
                  <Typography variant="body2">
                    <strong>Backup:</strong> {artifact.name} ({formatBackupSize(artifact.size)}, {formatShortDateTime(artifact.created_at)})
                  </Typography>
                )}
              </Box>

              {lastRun?.messages?.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                  {lastRun.messages.map((m, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LogLevelChip level={m.level} />
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>{m.msg}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {lastRun && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArticleOutlinedIcon fontSize="small" />}
                  onClick={handleViewLogs}
                >
                  Ver logs desta execução
                </Button>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default CronRow;
